import { useCallback, useEffect, useRef } from 'react';
import { BULK_UPLOAD_CONFIG } from './constants';
import { BulkUploadLogger } from './logging';
import { calculateBackoffInterval } from './utils';

interface UsePollingOptions<T> {
  /** The polling function to execute */
  pollFn: () => Promise<T>;
  /** Callback when polling succeeds */
  onSuccess?: (data: T) => void;
  /** Callback when polling fails */
  onError?: (error: Error) => void;
  /** Function to determine if polling should continue */
  shouldContinue: (data: T | null) => boolean;
  /** Enable polling */
  enabled: boolean;
  /** Initial interval in milliseconds */
  initialInterval?: number;
  /** Maximum interval in milliseconds (for exponential backoff) */
  maxInterval?: number;
  /** Enable exponential backoff */
  enableBackoff?: boolean;
}

/**
 * Custom hook for polling with exponential backoff
 * Fixed to prevent infinite loops from dependency array changes
 */
export function usePolling<T>({
  pollFn,
  onSuccess,
  onError,
  shouldContinue,
  enabled,
  initialInterval = BULK_UPLOAD_CONFIG.INITIAL_POLLING_INTERVAL_MS,
  maxInterval = BULK_UPLOAD_CONFIG.MAX_POLLING_INTERVAL_MS,
  enableBackoff = true,
}: UsePollingOptions<T>) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const nextIntervalRef = useRef<number>(initialInterval);
  const dataRef = useRef<T | null>(null);
  const isMountedRef = useRef<boolean>(true);

  // Store function refs to avoid dependency issues
  const pollFnRef = useRef(pollFn);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const shouldContinueRef = useRef(shouldContinue);

  // Track mounted state to prevent setting timeouts after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Update refs when functions change (doesn't trigger effect re-run)
  useEffect(() => {
    pollFnRef.current = pollFn;
  }, [pollFn]);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    shouldContinueRef.current = shouldContinue;
  }, [shouldContinue]);

  // Stable cleanup function
  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Main polling effect - only depends on enabled and config values
  useEffect(() => {
    // Clear any existing timeout
    cleanup();

    if (!enabled) {
      return () => {
        clearTimeout(timeoutRef.current ?? undefined);
        timeoutRef.current = null;
      };
    }

    // Check if we should continue polling based on existing data
    if (dataRef.current !== null && !shouldContinueRef.current(dataRef.current)) {
      return () => {
        clearTimeout(timeoutRef.current ?? undefined);
        timeoutRef.current = null;
      };
    }

    // Reset interval when starting
    nextIntervalRef.current = initialInterval;

    // Polling function using setTimeout chain to prevent race conditions
    const poll = async () => {
      try {
        const result = await pollFnRef.current();
        dataRef.current = result;

        if (onSuccessRef.current) {
          onSuccessRef.current(result);
        }

        // Check if we should continue polling
        if (!shouldContinueRef.current(result)) {
          // Stop polling
          cleanup();
          return;
        }

        // Schedule next poll with setTimeout chain
        if (isMountedRef.current) {
          if (enableBackoff) {
            // Calculate next interval for exponential backoff
            nextIntervalRef.current = calculateBackoffInterval(nextIntervalRef.current, maxInterval);
          }
          // Schedule the next poll - this ensures only one poll runs at a time
          // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout -- cleaned up via timeoutRef in the effect cleanup
          timeoutRef.current = setTimeout(poll, nextIntervalRef.current);
        }
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Polling failed');
        BulkUploadLogger.error('Polling failed', err);

        if (onErrorRef.current) {
          onErrorRef.current(err);
        }

        // Continue polling even after error (if still mounted and enabled)
        if (isMountedRef.current) {
          // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout -- cleaned up via timeoutRef in the effect cleanup
          timeoutRef.current = setTimeout(poll, nextIntervalRef.current);
        }
      }
    };

    // Start polling immediately with setTimeout(0)
    // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout -- cleaned up via timeoutRef in the effect cleanup
    timeoutRef.current = setTimeout(poll, 0);

    // Cleanup on unmount or when enabled changes
    return () => {
      clearTimeout(timeoutRef.current ?? undefined);
      timeoutRef.current = null;
    };
  }, [enabled, initialInterval, maxInterval, enableBackoff, cleanup]);

  return {
    data: dataRef.current,
    stop: cleanup,
    currentInterval: nextIntervalRef.current,
  };
}
